// Client-side routing and the URL contract for filters.
//
// The URL is the state (PRD 7.2: every view is a link). This module is the only place that
// touches history and window.location: components call navigate() or setFilters(), and the store
// is updated from whatever the URL says afterwards, never the other way round. That is what makes
// the back button work and what makes a pasted link land on the same screen with the same filters.
//
// It is also where the two tools are kept apart (Estimate Tool 2.2). Every route names the tool
// it belongs to, and the path prefix is what decides: a pasted `/estimate/hygiene` lands in the
// Estimate tool with the right nav item current, without the client having been told first.

import { store } from './store.js';
import { parseFilters, filtersToQuery, withFilters } from './lib/filters.js';

/** The tools, and where each one opens. The switcher's labels come from bootstrap; these are the
 *  fallback for a client that has not loaded it yet, and the home each tool navigates to. */
export const TOOLS = Object.freeze({
  forecast: Object.freeze({ id: 'forecast', label: 'Forecast', home: '/' }),
  estimate: Object.freeze({ id: 'estimate', label: 'Estimate', home: '/estimate' }),
});

/** The prefix that selects the Estimate tool. Forecast owns everything else. */
const ESTIMATE_PREFIX = '/estimate';

const cleanPath = (pathname) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname || '/';

/**
 * The tool a path belongs to. The path is the only thing that decides, so an unknown address
 * under `/estimate` still resolves to the Estimate tool and its not-found page keeps that tool's
 * navigation rather than dropping the reader into Forecast.
 */
export function toolForPath(pathname) {
  const path = cleanPath(pathname);
  return path === ESTIMATE_PREFIX || path.startsWith(`${ESTIMATE_PREFIX}/`)
    ? 'estimate'
    : 'forecast';
}

/** The tool the browser is in right now. */
export const currentTool = () => toolForPath(window.location.pathname);

const inTool = (tool, routes) => Object.freeze(routes.map((r) => Object.freeze({ ...r, tool })));

/** Forecast's primary navigation, in PRD 9 order. `phase` is the phase that fills the page in. */
export const NAV = inTool('forecast', [
  { path: '/', component: 'page-dashboard', label: 'Dashboard', phase: 22 },
  { path: '/timeline', component: 'page-timeline', label: 'Timeline', phase: 26 },
  { path: '/heatmap', component: 'page-heatmap', label: 'Heatmap', phase: 23 },
  { path: '/assignments', component: 'page-assignments', label: 'Assignments', phase: 28 },
  { path: '/bench', component: 'page-bench', label: 'Bench', phase: 29 },
  { path: '/changes', component: 'page-changes', label: 'Changes', phase: 30 },
  { path: '/projects', component: 'page-projects', label: 'Project Intelligence', phase: 31 },
  { path: '/scenarios', component: 'page-scenarios', label: 'Scenarios', phase: 35 },
  { path: '/hygiene', component: 'page-hygiene', label: 'Data Hygiene', phase: 34 },
  { path: '/settings', component: 'page-settings', label: 'Settings', phase: 19 },
]);

/**
 * Estimate's primary navigation (Estimate Tool 2.2). Three destinations at first; Deals joined
 * them on 2026-09-29 (Estimate Tool 13): the HubSpot deals, estimated from what they carry.
 */
export const ESTIMATE_NAV = inTool('estimate', [
  { path: '/estimate', component: 'page-estimates', label: 'Estimates', phase: 'E6' },
  { path: '/estimate/deals', component: 'page-estimate-deals', label: 'Deals', phase: 'E17' },
  {
    path: '/estimate/hygiene',
    component: 'page-estimate-hygiene',
    label: 'Data Hygiene',
    phase: 'E2',
  },
  {
    path: '/estimate/settings',
    component: 'page-estimate-settings',
    label: 'Settings',
    phase: 'E4',
  },
]);

/** The primary navigation each tool shows, keyed by tool id. */
export const NAV_BY_TOOL = Object.freeze({ forecast: NAV, estimate: ESTIMATE_NAV });

/** Pages that exist but stay out of the primary bar. */
const EXTRA_ROUTES = inTool('forecast', [
  { path: '/styleguide', component: 'page-styleguide', label: 'Style guide' },
  // The individual view's picker (PRD 7.2; Phase 32). Not one of the ten destinations: a person
  // is reached from the heatmap's and the matrix's rows, and the Heatmap item stays lit here.
  { path: '/people', component: 'page-people', label: 'People', under: '/heatmap', phase: 32 },
  // The opportunity funnel (PRD 10.4; Phase 33). Also not one of the ten: it is the Bench's
  // pipeline line opened up — what is in it, by stage — so the Bench item stays lit and the
  // bench card links here.
  {
    path: '/funnel',
    component: 'page-funnel',
    label: 'Opportunity funnel',
    under: '/bench',
    phase: 33,
  },
]);

/**
 * Routes that carry a parameter. The estimate editor is a route and not a modal (Estimate Tool
 * 2.2): an estimate is the thing this tool exists to produce, and a modal would make it
 * unlinkable. `/estimate/new` is listed before the pattern so the literal wins.
 */
const PARAM_ROUTES = [
  // Project Intelligence (PRD 10.5; Phase 31): one project's panel, beneath the picker. A route
  // and not a drawer, so "where did this number come from" is a link a planner can send — and
  // so the two screens that name a project for a reason can send a reader straight here: the
  // dashboard's top risk projects, and the Data Hygiene worklist's project rows.
  ...inTool('forecast', [
    {
      pattern: /^\/projects\/([0-9a-fA-F-]{36})$/,
      keys: ['id'],
      component: 'page-project',
      label: 'Project',
      under: '/projects',
    },
    // The individual view (PRD 7.2; Phase 32): one person's weeks, a link a manager can send.
    {
      pattern: /^\/people\/([0-9a-fA-F-]{36})$/,
      keys: ['id'],
      component: 'page-person',
      label: 'Person',
      under: '/heatmap',
    },
  ]),
  ...inTool('estimate', [
    { path: '/estimate/new', component: 'page-estimate-editor', label: 'New estimate' },
    // The Template Designer (Estimate Tool 8.10; Phase E14): the six rungs, and one rung's
    // template. Under Settings, because that is where the design says it is reached from, and
    // because changing a template is the same kind of act as changing a setting.
    {
      path: '/estimate/settings/templates',
      component: 'page-estimate-templates',
      label: 'Schedule templates',
      under: '/estimate/settings',
    },
    {
      pattern:
        /^\/estimate\/settings\/templates\/(landing_page|microsite|small|medium|large|enterprise)$/,
      keys: ['tier'],
      component: 'page-estimate-template-designer',
      label: 'Template designer',
      under: '/estimate/settings',
    },
    // Create Schedule (Estimate Tool 8.1) is a route under the estimate, not a modal on it: the
    // outline is 72 lines a producer reads, argues with and comes back to, and a plan that could
    // not be linked to could not be reviewed by anyone but the person who generated it. Listed
    // before the bare-id pattern, which would otherwise never match it but reads clearer this way.
    // A deal's estimate (Estimate Tool 13; Phase E17): a route under Deals, so an estimate a
    // planner argues with is a link they can send.
    {
      pattern: /^\/estimate\/deals\/([0-9a-fA-F-]{36})$/,
      keys: ['id'],
      component: 'page-estimate-deal',
      label: 'Deal estimate',
      under: '/estimate/deals',
    },
    {
      pattern: /^\/estimate\/([0-9a-fA-F-]{36})\/schedule$/,
      keys: ['id'],
      component: 'page-estimate-schedule',
      label: 'Create Schedule',
      under: '/estimate',
    },
    {
      pattern: /^\/estimate\/([0-9a-fA-F-]{36})$/,
      keys: ['id'],
      component: 'page-estimate-editor',
      label: 'Estimate',
      // The nav item that stays lit while this route is showing.
      under: '/estimate',
    },
  ]),
];

const ROUTES = [...NAV, ...ESTIMATE_NAV, ...EXTRA_ROUTES, ...PARAM_ROUTES];

/** The registered route for an exact path, or null. */
export function routeFor(path) {
  return ROUTES.find((r) => r.path === path) ?? null;
}

/**
 * The route for a pathname, with any parameters it carried; unknown paths get the not-found
 * page, in their own tool. Literal paths are matched before patterns, so `/estimate/new` is the
 * new-estimate page rather than an estimate whose id is the word "new".
 */
export function matchRoute(pathname) {
  const clean = cleanPath(pathname);
  const exact = routeFor(clean);
  if (exact) return { ...exact, params: {} };

  for (const route of PARAM_ROUTES) {
    if (!route.pattern) continue;
    const hit = route.pattern.exec(clean);
    if (!hit) continue;
    const params = Object.fromEntries(route.keys.map((key, i) => [key, hit[i + 1]]));
    return { ...route, path: clean, params };
  }
  return { path: clean, component: 'page-not-found', tool: toolForPath(clean), params: {} };
}

/**
 * Whether a nav item should read as current for a pathname. A route beneath a destination keeps
 * that destination lit — an estimator editing an estimate has not left Estimates.
 */
export function isCurrentNav(itemPath, pathname) {
  const clean = cleanPath(pathname);
  if (clean === itemPath) return true;
  return matchRoute(clean).under === itemPath;
}

/** Navigate to a path, carrying the current filters unless the caller says otherwise. */
export function navigate(path, { keepFilters = true, replace = false } = {}) {
  // The filters of PRD 7.2 are Forecast's; none of them applies to an estimate (Estimate Tool
  // 2.3). Carrying them across the switcher would put state in an Estimate link that no screen
  // there reads, and would hand it back to Forecast on the way out.
  const carry = keepFilters && toolForPath(path) === 'forecast';
  const query = carry ? filtersToQuery(store.get().filters) : '';
  const url = `${path}${query}`;
  if (replace) history.replaceState(null, '', url);
  else history.pushState(null, '', url);
  window.dispatchEvent(new CustomEvent('waag:navigate'));
}

/**
 * Change filters. The new state goes into the URL first and is read back from it, so what the
 * components see is always exactly what the link would reproduce.
 */
export function setFilters(patch, { replace = false } = {}) {
  const next = withFilters(store.get().filters, patch);
  const url = `${window.location.pathname}${filtersToQuery(next)}`;
  // Filter changes replace by default at the same path would bury the back button under every
  // keystroke; pushing is what makes "undo my filter" work, so it is the default here.
  if (replace) history.replaceState(null, '', url);
  else history.pushState(null, '', url);
  window.dispatchEvent(new CustomEvent('waag:navigate'));
}

/** Read the URL into the store. */
export function syncFromUrl() {
  store.setFilters(parseFilters(window.location.search));
}

// The store follows the URL from the moment this module loads, not from the moment a router
// element mounts: a page that reads filters without a router on screen (a print view, a test, a
// view rendered on its own) would otherwise see stale state.
window.addEventListener('popstate', syncFromUrl);
window.addEventListener('waag:navigate', syncFromUrl);
syncFromUrl();

/** Call `fn` on every URL change, and once now. Returns a teardown. */
export function onNavigate(fn) {
  window.addEventListener('popstate', fn);
  window.addEventListener('waag:navigate', fn);
  fn();
  return () => {
    window.removeEventListener('popstate', fn);
    window.removeEventListener('waag:navigate', fn);
  };
}

/** Wire a router element to history. Returns a teardown. */
export function startRouter(routerEl) {
  return onNavigate(() => {
    routerEl.route = matchRoute(window.location.pathname);
    routerEl.requestUpdate?.();
  });
}
